/*
 * listener date 정의.
 * [
 *   {
 *      action: ('add' or 'update' or 'delete' or 'set') (set - replaceAll)
 *              mailbox - email - add, delete, update.
 *              dataManager - email - create, modify, destroy. 로 구분.
 *      type: (all, thread, email, mailbox 등.)
 *      data: { ... } item, list, map 등.
 *      target: 이벤트가 발생한 객체. (mail, mailbox 등)
 *   }
 * ]
 *
 */

// MOVING, DELETING 상태일 때는 메일을 표시하지 않는다. (메일 목록에서 제외)
// 여기서 관리되는 state는 이메일 각각의 일시적인 상태로, 실제 메일의 상태와는 차이가 있을 수 있다.
// 정확하게 설명하면, 서버에 요청을 보내고 응답을 받기 전까지의 상태를 의미한다.
// 예를 들어 DELETING 상태인 메일은 전체 count에 포함되어 있는 상태이다.
export const EMAIL_STATE = {
  NORMAL: 0b00, // MOVING | DELETING 비트가 없는 경우 NORMAL로 간주.
  MOVING: 0b01,
  DELETING: 0b10,
  DELETE_MASK: 0b11,
  // 아래의 상태는 별개로 동작.
  FLAGGING: 0b0100,
  UNFLAGGING: 0b1000,
  READING: 0b010000,
  UNREADING: 0b100000,
  KEYWORD_MASK: 0b111100,
}

export const TYPE = {
  MAILBOX: 0,
  EMAIL: 1,
  THREAD: 2,
  DATAMANAGER: 3,
  IDENTITY: 4,
}

export const ACTION = {
  DELETE: 0,
  UPDATE: 1,
  ADD: 2,
  DESTROY: 3,
  MODIFY: 4,
  CREATE: 5,
  SET: 6,
  SAVE: 7,
  RELOAD: 8,
  PAGELOADED: 9,
}

type ListenerFunction = (data: any) => void

interface Owner {
  [key: string]: any
  getAllDataEvent?: (name: string) => any
}

export function ListenerObj(this: any, name: string, owner?: Owner) {
  const self = this

  self.name = name
  self.listeners = [] as ListenerFunction[]

  self.notify = (data: any) => {
    self.listeners.forEach((listener: ListenerFunction) => {
      listener(data)
    })
  }

  self.add = (listener: ListenerFunction) => {
    if (self.listeners.indexOf(listener) < 0) {
      self.listeners.push(listener)
    }
  }

  self.remove = (listener: ListenerFunction) => {
    const pos = self.listeners.indexOf(listener)
    if (pos >= 0) {
      self.listeners.splice(pos, 1)
    }
  }

  self.init = () => {
    if (owner != null) {
      owner[`add${self.name}Listener`] = (
        listener: ListenerFunction,
        skipDefaultLoading = true,
      ) => {
        self.add(listener)

        // 바로 호출하면 state변경 시점 문제로 오류가 발생할 수 있다.
        if (skipDefaultLoading === true) {
          setTimeout(() => {
            if (owner.getAllDataEvent != null) {
              const data = owner.getAllDataEvent(self.name)
              if (data != null) {
                listener(data)
              }
            }
          }, 100)
        }
      }
      owner[`remove${self.name}Listener`] = self.remove
    }
  }

  self.init()
}

interface JmapStateObj {
  enableQueryChange?: any
  lastState?: any
  lastQueryState?: any
  isRealMailbox: boolean
  getLastState: () => any
  setLastState: (state: any) => void
  getLastQueryState: () => any
  setLastQueryState: (state: any) => void
}

export function extendJmapState(obj: any): JmapStateObj {
  const self = obj

  self.enableQueryChange = null

  self.lastState = undefined
  self.lastQueryState = undefined

  self.isRealMailbox = true

  self.getLastState = () => {
    return self.lastState
  }

  self.setLastState = (state: any) => {
    self.lastState = state
  }

  self.getLastQueryState = () => {
    return self.lastQueryState
  }

  self.setLastQueryState = (state: any) => {
    self.lastQueryState = state
  }

  return self
}

export interface EmailData {
  data: {
    id: string
  }
  getViewTime: () => string
  // OMD-151: 자기발신 메일 동일 시각 tie-break용. true=보낸, false=받은, null=불명.
  isSentMail: () => boolean | null
  // OMD-151: 자기발신 쌍(동일 Message-ID) 식별용. 없으면 null.
  getMessageId: () => string | null
}

export function isEqualData(email1: EmailData | null, email2: EmailData | null): boolean {
  if (email1 == null && email2 == null) {
    return true
  }

  if (email1 == null || email2 == null) {
    return false
  }

  return email1.data.id === email2.data.id
}

export function applyData(from: any, to: any): boolean {
  const targetData = to

  let isChanged = false

  Object.entries(from).forEach(([key, value]) => {
    if (targetData[key] !== value) {
      isChanged = true
    }
    targetData[key] = value
  })

  return isChanged
}

// OMD-151: 자기발신 메일 tie-break 우선순위. 받은(false)=0 → 보낸(true)=1 → 불명(null)=2.
const sentMailRank = (value: boolean | null): number => {
  if (value === false) {
    return 0
  }
  if (value === true) {
    return 1
  }
  return 2
}

export function sortByReceivedAt(a: EmailData | null, b: EmailData | null): number {
  if (a == null || b == null) {
    return 0
  }

  // OMD-151: 동일 Message-ID(자기발신 쌍)면 시각보다 우선해 받은/보낸 순으로 고정한다.
  // 자기발신 시 받은 사본의 receivedAt이 보낸 사본보다 작아, 시각만으로는 보낸 사본이
  // 위로 와 순서가 뒤바뀌는 문제가 있어 시각 비교 앞단에서 처리한다.
  const messageIdA = a.getMessageId()
  const messageIdB = b.getMessageId()
  if (messageIdA != null && messageIdB != null && messageIdA === messageIdB) {
    return sentMailRank(a.isSentMail()) - sentMailRank(b.isSentMail())
  }

  if (a.getViewTime() < b.getViewTime()) {
    return 1
  } else if (a.getViewTime() > b.getViewTime()) {
    return -1
  }

  // 동일 시각 tie-break: 받은 메일 → 보낸 메일 → 불명 순으로 앞에 온다.
  // rank가 같으면 0을 반환해 안정 정렬이 기존 순서를 보존한다.
  return sentMailRank(a.isSentMail()) - sentMailRank(b.isSentMail())
}
